/** Standard work day: 8:00–12:00 + 13:00–17:00, lunch 12:00–13:00 (matches payroll). */
export const WORK_START_HOUR = 8;
export const LUNCH_START_HOUR = 12;
export const LUNCH_END_HOUR = 13;
export const WORK_END_HOUR = 17;

export type DayLateUndertime = {
  /** Raw minutes late vs schedule start (before offset). */
  minutesLate: number;
  /** Raw minutes past 17:00 (before offset / OT flag). */
  overtime: number;
  /** minutesLate − overtime. Positive → payslip late; negative + OT on → payslip OT. */
  timeDiff: number;
  /** Payslip late minutes (timeDiff when positive, else 0). */
  lateMinutes: number;
  /** Leave-early minutes vs session end (12:00 morning half, 17:00 otherwise). */
  undertimeMinutes: number;
  /** Chargeable OT for payslip: −timeDiff when negative and OT is honored, else 0. */
  overtimeMinutes: number;
  /** 0, 0.5, or 1 based on morning/afternoon sessions worked. */
  dayCredit: number;
};

const atTime = (date: Date, hour: number, minute = 0) => {
  const result = new Date(date);
  result.setHours(hour, minute, 0, 0);
  return result;
};

/**
 * Attendance times are Philippine wall-clock numbers. The API may append Z
 * because DateTime.UtcNow.AddHours(8) keeps Kind=Utc; browsers then show +8h.
 */
export const parseAttendanceDateTime = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?/
  );
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second ?? 0)
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatAttendanceTime = (value?: string | null): string => {
  const date = parseAttendanceDateTime(value);
  if (!date) return '—';
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

/**
 * Same-day out before in is the 24-hour AM/PM mix-up (5:00 → 17:00).
 * Overnight outs must use the next calendar date.
 */
export const interpretAfternoonOut = (timeIn: Date, timeOut: Date): Date => {
  if (
    timeOut.getFullYear() !== timeIn.getFullYear() ||
    timeOut.getMonth() !== timeIn.getMonth() ||
    timeOut.getDate() !== timeIn.getDate()
  ) {
    return timeOut;
  }
  if (timeOut.getTime() > timeIn.getTime()) return timeOut;
  if (timeOut.getHours() >= 12) return timeOut;
  const adjusted = new Date(timeOut);
  adjusted.setHours(adjusted.getHours() + 12);
  return adjusted;
};

/**
 * Late minutes: clock hour/minute only (Excel HOUR/MINUTE; seconds discarded).
 * Equivalent to flooring TotalMinutes when the schedule boundary is on the minute.
 */
const floorMinutesBetween = (from: Date, to: Date) =>
  Math.max(0, Math.floor((to.getTime() - from.getTime()) / 60000));

/**
 * Stay-past-end / leave-early minutes: nearest minute, half away from zero (Excel ROUND).
 * Math.round matches Excel ROUND for non-negative values.
 */
const roundMinutesBetween = (from: Date, to: Date) => {
  const mins = (to.getTime() - from.getTime()) / 60000;
  if (mins <= 0) return 0;
  return Math.round(mins);
};

/**
 * Late may be offset by staying past 17:00 (e.g. in 08:12 / out 17:12 = 8 hrs,
 * no late, no OT). Time diff = minutesLate − overtime. Positive diff is payslip
 * late; negative diff is payslip OT only when overtime is honored.
 */
export const computeLateUndertime = (
  timeIn?: string | null,
  timeOut?: string | null,
  overtimeAllowed = false
): DayLateUndertime | null => {
  if (!timeIn || !timeOut) return null;

  const start = parseAttendanceDateTime(timeIn);
  let end = parseAttendanceDateTime(timeOut);
  if (!start || !end) return null;
  end = interpretAfternoonOut(start, end);

  const morningStart = atTime(start, WORK_START_HOUR);
  const lunchStart = atTime(start, LUNCH_START_HOUR);
  const lunchEnd = atTime(start, LUNCH_END_HOUR);
  const dayEnd = atTime(start, WORK_END_HOUR);

  // Clock-in during lunch (12:00–13:00) counts as starting at 13:00.
  const effectiveStart =
    start >= lunchStart && start < lunchEnd ? lunchEnd : start;

  const workedMorning = effectiveStart < lunchStart && end > morningStart;
  const workedAfternoon = end > lunchEnd && effectiveStart < dayEnd;
  const dayCredit = (workedMorning ? 0.5 : 0) + (workedAfternoon ? 0.5 : 0);
  if (dayCredit === 0) {
    return {
      minutesLate: 0,
      overtime: 0,
      timeDiff: 0,
      lateMinutes: 0,
      undertimeMinutes: 0,
      overtimeMinutes: 0,
      dayCredit: 0
    };
  }

  let minutesLate = 0;
  if (workedMorning) {
    minutesLate = floorMinutesBetween(morningStart, effectiveStart);
  } else if (workedAfternoon && effectiveStart > lunchEnd) {
    minutesLate = floorMinutesBetween(lunchEnd, effectiveStart);
  }

  const leaveEarly =
    workedMorning && !workedAfternoon
      ? roundMinutesBetween(end, lunchStart)
      : workedAfternoon
        ? roundMinutesBetween(end, dayEnd)
        : 0;

  const overtime = roundMinutesBetween(dayEnd, end);
  const timeDiff = minutesLate - overtime;

  return {
    minutesLate,
    overtime,
    timeDiff,
    lateMinutes: Math.max(0, timeDiff),
    undertimeMinutes: leaveEarly,
    overtimeMinutes: overtimeAllowed && timeDiff < 0 ? -timeDiff : 0,
    dayCredit
  };
};

export const formatMinutes = (minutes: number | null | undefined): string => {
  if (minutes == null || minutes <= 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours <= 0) return `${mins}m`;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
};

/** Format a signed minute value (e.g. time diff); 0 shows as —. */
export const formatSignedMinutes = (
  minutes: number | null | undefined
): string => {
  if (minutes == null || minutes === 0) return '—';
  const sign = minutes < 0 ? '−' : '';
  return `${sign}${formatMinutes(Math.abs(minutes))}`;
};

/**
 * Clock-in during lunch (12:00–13:00) is treated as 13:00 for hours/late calc.
 */
export const getEffectiveWorkStart = (timeIn: Date): Date => {
  const lunchStart = atTime(timeIn, LUNCH_START_HOUR);
  const lunchEnd = atTime(timeIn, LUNCH_END_HOUR);
  if (timeIn >= lunchStart && timeIn < lunchEnd) return lunchEnd;
  return timeIn;
};

/** Elapsed work display; lunch-window clock-ins start counting at 13:00. */
export const formatHoursWorked = (
  timeIn?: string | null,
  timeOut?: string | null
): string => {
  if (!timeIn || !timeOut) return '—';
  const rawStart = parseAttendanceDateTime(timeIn);
  let end = parseAttendanceDateTime(timeOut);
  if (!rawStart || !end) return '—';
  end = interpretAfternoonOut(rawStart, end);
  const start = getEffectiveWorkStart(rawStart);

  const minutes = Math.floor((end.getTime() - start.getTime()) / 60000);
  if (minutes <= 0) return '—';
  return formatMinutes(minutes);
};
